import isEqual from 'react-fast-compare';

import {ANY_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {isChemSearch} from 'chemistry/ChemSearchUtils';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isSearchableSsiType} from 'constants/RegistryConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {isBLASTSearch} from 'search/blast/BLASTUtil';
import * as FilterUtil from 'search/FilterUtil';
import {hasNonDefaultSorts} from 'search/getSortsOrDefault';
import {
  Filter,
  FilterErrors,
  FilterKey,
  isFieldFilter,
  isStandardFilter,
  isTextFieldType,
  keyToEmptyFilter,
  MultiValueFilter,
  Operator,
  SearchParams,
  SearchType,
  SortKey,
  STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY,
  StandardRelativeRangeOperatorValue,
  VALUELESS_OPERATORS,
} from 'search/SearchConstants';

export function isQuerySearch(searchParams: SearchParams): boolean {
  return Boolean(searchParams.query);
}

export function clearSearchParams(
  searchParams: SearchParams,
  options?: {
    preservedFilterKeys?: Array<FilterKey>;
    preserveLockedFilters?: boolean;
    filtersToReset?: Array<Filter>;
  }
): SearchParams {
  const preservedFilterKeys = options?.preservedFilterKeys ?? [];
  const clearedFilters = searchParams.filters.filter(
    (filter) => preservedFilterKeys.includes(filter.key) || (options?.preserveLockedFilters && filter.locked)
  );
  const query = '';
  return {
    ...searchParams,
    sorts: [],
    query,
    filters: options?.filtersToReset
      ? FilterUtil.replaceByKey(clearedFilters, ...options.filtersToReset)
      : clearedFilters,
    offset: 0,
    groupBy: null,
  };
}

/**
 * Returns whether the search has a query, any non-empty filters that are not in skippedFilterKeys or
 * non-empty sorts.
 */
export function hasQueryOrNonDefaultParameters(
  searchParams: SearchParams,
  skippedFilterKeys: Array<FilterKey>,
  defaultTypes: Array<string>,
  searchType: SearchType
): boolean {
  return (
    hasQueryOrNonDefaultFilters(searchParams, skippedFilterKeys, defaultTypes) ||
    hasNonDefaultSorts(searchParams.sorts, searchType)
  );
}

/**
 * Returns whether the search has a query or any non-empty filters that are not in skippedFilterKeys.
 */
export function hasQueryOrNonDefaultFilters(
  searchParams: SearchParams,
  skippedFilterKeys: Array<FilterKey>,
  defaultTypes: Array<string>
): boolean {
  return (
    isQuerySearch(searchParams) ||
    hasNonDefaultFilters(searchParams.filters, skippedFilterKeys, defaultTypes) ||
    Boolean(searchParams.groupBy)
  );
}

/**
 * True iff any filter in filters is neither a default type filter nor has its key are specified in
 * skippedFilterKeys.
 */
export function hasNonDefaultFilters(
  filters: Array<Filter>,
  skippedFilterKeys: Array<FilterKey>,
  defaultTypes: Array<string>
): boolean {
  const hasCustomTypesFilter = Boolean(defaultTypes) && !hasDefaultTypesFilter(filters, defaultTypes);
  const hasOtherNonDefaultFilters = sanitizeEmptyFilters(filters).some(
    (filter) => ![...skippedFilterKeys, FilterKey.TYPES].includes(filter.key)
  );
  return hasCustomTypesFilter || hasOtherNonDefaultFilters;
}

export function hasDefaultTypesFilter(filters: Array<Filter>, defaultTypes: Array<string>): boolean {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter;
  if (!typesFilter || typesFilter.value.length === 0) {
    return true;
  }
  return defaultTypes.every((type) => typesFilter.value.includes(type) || isSearchableSsiType(type));
}

export const getFilterValue = (filter: Filter): {value: Filter['value']} => ({
  value: filter.value,
});

function standardRelativeRangeIsComplete(value: StandardRelativeRangeOperatorValue): boolean {
  // A relative range filter is complete if it has at least one boundary, and that boundary has both units
  // and a value. If either are missing a value, the filter should be considered incomplete.
  return Boolean(
    (value.fromUnits || value.toUnits) &&
      ((value.fromUnits && value.from) || (!value.fromUnits && !value.from)) &&
      ((value.toUnits && value.to) || (!value.toUnits && !value.to))
  );
}

export function sanitizeEmptyFilters(filters: Array<Filter>): Array<Filter> {
  return filters.filter((filter) => {
    if (isStandardFilter(filter)) {
      if (
        filter.key === FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION ||
        filter.key === FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER
      ) {
        // Since these are boolean filters, we check to make sure the filter's operator is not the same
        // as the default before sanitizing.
        return filter.operator !== keyToEmptyFilter(filter.key).operator;
      }
      const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[filter.operator];
      if (!defaultValueFactory) {
        throw new Error(
          `Attempted to retrieve a default value for standard filter operator ${filter.operator}, but no default value factory exists in STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY`
        );
      }

      // Support backcompat for old default values
      if (isFieldFilter(filter)) {
        if (isTextFieldType(filter.fieldType)) {
          if ([Operator.INCLUDES_TEXT, Operator.EXCLUDES_TEXT].includes(filter.operator)) {
            if (isEqual(filter.value, '')) {
              // new default value is []
              return false;
            }
          }
          if ([Operator.IS, Operator.IS_NOT].includes(filter.operator)) {
            if (isEqual(filter.value, '')) {
              // new default value is null
              return false;
            }
          }
        }

        if ([FieldType.DATE, FieldType.DATETIME].includes(filter.fieldType)) {
          if (isEqual(filter.value, '')) {
            // new default value is null
            return false;
          }
        }
      }

      return (
        VALUELESS_OPERATORS.includes(filter.operator) ||
        (!isEqual(defaultValueFactory(), filter.value) &&
          (filter.operator !== Operator.RELATIVE_RANGE ||
            standardRelativeRangeIsComplete(filter.value as StandardRelativeRangeOperatorValue)))
      );
    }
    // The TOP_LEVEL_PERMISSIONS, STARRED, and PROCESS_TASK_HAS_FAILED_MAPPINGS filters should not be filtered
    // out since they have a constant value. The ROOT_PROCESS_TASK_SCHEMA filter can have an empty value to
    // represent all possible values
    if (
      filter.key === FilterKey.STARRED ||
      filter.key === FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS ||
      filter.key === FilterKey.ROOT_PROCESS_TASK_SCHEMA ||
      filter.key === FilterKey.TOP_LEVEL_PERMISSIONS
    ) {
      return true;
    }
    if (filter.key === FilterKey.CONTAINER_CONTENTS) {
      return filter.value.contentIds.length > 0;
    }
    if (filter.operator === Operator.IS_EMPTY || filter.operator === Operator.IS_NOT_EMPTY) {
      return true;
    }
    // In REVIEW_LIFECYCLE_TYPE empty values means find any project with any associated
    // review lifecycle.
    if (filter.key === FilterKey.REVIEW_LIFECYCLE_TYPE) {
      return true;
    }
    if (filter.key === FilterKey.ALPHAFOLD_STRUCTURE) {
      return true;
    }

    return !isEqual(getFilterValue(filter), getFilterValue(keyToEmptyFilter(filter.key)));
  });
}

function sanitizeInvalidFilters(filters: Array<Filter>, filterErrors: FilterErrors): Array<Filter> {
  assert(filters.length === filterErrors.length, 'Expected filters and filterErrors to have the same length');
  return filters.filter((filter, index) => !filterErrors[index]);
}

export function sanitizeFilters(
  filters: Array<Filter>,
  filterErrors: FilterErrors,
  {
    includeArchiveFilter = true,
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter = true,
    includeProcessesIsSystemDataFilter = true,
  }: {
    includeArchiveFilter?: boolean;
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter?: boolean;
    includeProcessesIsSystemDataFilter?: boolean;
  } = {includeArchiveFilter: true, includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter: true}
): Array<Filter> {
  const sanitizedFilters = sanitizeEmptyFilters(sanitizeInvalidFilters(filters, filterErrors));
  if (includeArchiveFilter) {
    const archiveFilter = sanitizedFilters.find((filter) => filter.key === FilterKey.ARCHIVE_PURPOSES);
    if (!archiveFilter) {
      sanitizedFilters.push(keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES));
    } else {
      // If the ArchivePurposes filter is functionally empty, meaning no purposes are selected,
      // then all archived and non-archived items will be included
      const purposeStrings = archiveFilter.value as Array<string>;
      if (purposeStrings.length === 0) {
        archiveFilter.value = [ANY_ARCHIVED_PURPOSE_STRING];
      }
    }
  }
  if (includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter) {
    const isAssociatedWithUnsubmittedRequestV2SubmissionFilter = sanitizedFilters.find(
      (filter) => filter.key === FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION
    );
    if (!isAssociatedWithUnsubmittedRequestV2SubmissionFilter) {
      sanitizedFilters.push({
        ...keyToEmptyFilter(FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION),
        operator: Operator.IS_FALSE,
      });
    }
  }
  if (includeProcessesIsSystemDataFilter) {
    const processesIsSystemDataFilter = sanitizedFilters.find(
      (filter) => filter.key === FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER
    );
    if (!processesIsSystemDataFilter) {
      sanitizedFilters.push({
        ...keyToEmptyFilter(FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER),
        operator: Operator.IS_FALSE,
      });
    }
  }
  const typeFilterIndex = sanitizedFilters.findIndex(
    (filter) => filter.key === FilterKey.TYPES || filter.key === FilterKey.TYPE
  );

  if (typeFilterIndex !== -1) {
    const typeFilter = sanitizedFilters[typeFilterIndex];
    const typeFilterValue = typeFilter.value as Array<string>;

    if (typeFilterValue.length !== 0) {
      const filteredTypeValue = typeFilterValue.map((type) =>
        // Replace explicit SSI types with basic folder item type and de-dup.
        isSearchableSsiType(type) ? ModelTypeConstants.BASIC_FOLDER_ITEM : type
      );

      sanitizedFilters[typeFilterIndex] = {
        ...typeFilter,
        value: [...new Set(filteredTypeValue)],
      } as MultiValueFilter;
    }
  }
  return sanitizedFilters;
}

function sanitizeSorts(searchParams: SearchParams): SearchParams {
  // BLAST and chemical sorts only apply when the respective filter is present, so remove sorts if the
  // filter was sanitized away.
  if (
    (searchParams.sorts.some((sort) => sort.key === SortKey.SORT_BLAST_E_VALUE) &&
      !isBLASTSearch(searchParams)) ||
    (searchParams.sorts.some((sort) => sort.key === SortKey.SORT_CHEMICAL_SIMILARITY) &&
      !isChemSearch(searchParams))
  ) {
    return {
      ...searchParams,
      sorts: [],
    };
  }
  return searchParams;
}

export function sanitizeSearchParams(
  searchParams: SearchParams,
  filterErrors: FilterErrors,
  options: {
    includeArchiveFilter?: boolean;
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter?: boolean;
    includeProcessesIsSystemDataFilter?: boolean;
  } = {
    includeArchiveFilter: true,
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter: true,
    includeProcessesIsSystemDataFilter: true,
  }
): SearchParams {
  const sanitizedFilters = sanitizeFilters(searchParams.filters, filterErrors, options);
  const query = searchParams.query || '';
  // Apply sort sanitization after all other sanitization because some sorts are only applicable when using
  // specific filters, which may have been removed above.
  return sanitizeSorts({
    ...searchParams,
    query,
    filters: sanitizedFilters,
    // Make sure groupBy and nextToken are added as a field if it was omitted so that we can treat implicit
    // and explicit omission as equal for purposes of tests, triggering new searches, and component state
    // updates.
    groupBy: searchParams.groupBy || null,
    nextToken: searchParams.nextToken || null,
  });
}

export function mergeFiltersToSearchParams(searchParams: SearchParams, filters: Array<Filter>): SearchParams {
  return {
    ...searchParams,
    filters: FilterUtil.replaceByKey(searchParams.filters, ...filters),
  };
}
